import { API, graphqlOperation } from 'aws-amplify';
import gql from 'graphql-tag';
import { GraphQLResult } from '@aws-amplify/api-graphql';
import {
  createStageCategory,
  updateStageCategory,
} from '../../graphql/mutations';

// Define interfaces for the data structures
interface Disposition {
  id: string;
  categoryID: string;
  isSetupDeal?: boolean;
  order: number;
  stageID?: string;
  formName?: string;
  iconName?: string;
  isConversation?: boolean;
  expiresIn?: number;
  isNotSelectable?: boolean;
  isDefault?: boolean;
  deleteType?: string;
  requireDate?: boolean;
  noRecording?: boolean;
  title: string;
  isDeleted?: boolean;
}

interface Stage {
  id: string;
  categoryID: string;
  calendarAppointmentStatus?: string;
  changeToDispositionID?: string;
  description?: string;
  iconName?: string;
  isActive?: boolean;
  isDisplay?: boolean;
  isMapDefault?: boolean;
  isNoDealDefault?: boolean;
  requireCloser?: boolean;
  sendToCRM?: boolean;
  sequence?: number;
  userSelectable?: boolean;
  flexAttributes?: string;
  title: string;
  isDeleted?: boolean;
}

interface CategoryData {
  id: string;
  title?: string;
  isDefault?: boolean;
  flexAttributes?: any; // Changed to any to handle both string and parsed object
  enablePindrop?: boolean;
  enableDeal?: boolean;
  orgID?: string;
  isDeleted?: boolean;
}

export interface Category extends CategoryData {
  dispositions: Disposition[] | { items: Disposition[]; nextToken?: string };
  stages: Stage[] | { items: Stage[]; nextToken?: string };
  dispositionDependentStageTitles?: Record<string, string[]>;
  // Dictionary mapping disposition IDs to rule titles
  dependentKPIRules?: Record<string, string[]>;
}

export const StageRepository = {
  /**
   * Fetch stage categories with all related data for an organization
   * @param {string} orgID - Organization ID
   * @returns {Promise<Array>} - List of stage categories with dispositions and stages
   */
  fetchStageCategories: async (orgID: string): Promise<Category[]> => {
    const stageQuery = gql`
      query MyQuery($orgID: ID!, $nextToken: String) {
        listStageCategorys(
          filter: { orgID: { eq: $orgID }, isDeleted: { eq: false } }
          limit: 10000
          nextToken: $nextToken
        ) {
          items {
            id
            title
            isDefault
            flexAttributes
            enablePindrop
            enableDeal
            defaultProductFamily
            dispositions(filter: { isDeleted: { eq: false } }) {
              items {
                id
                categoryID
                isSetupDeal
                order
                stageID
                formName
                iconName
                isConversation
                expiresIn
                isNotSelectable
                isDefault
                deleteType
                requireDate
                noRecording
                title
                isDeleted
                groupID
              }
              nextToken
            }
            stages(filter: { isDeleted: { eq: false } }) {
              items {
                id
                categoryID
                calendarAppointmentStatus
                changeToDispositionID
                description
                iconName
                isActive
                isDisplay
                isMapDefault
                isNoDealDefault
                requireCloser
                setStages
                clearStages
                sendToCRM
                sequence
                userSelectable
                flexAttributes
                title
                isDeleted
              }
              nextToken
            }
            orgID
            isDeleted
          }
          nextToken
        }
      }
    `;

    try {
      let nextToken: string | null = null;
      let categories: Category[] = [];

      do {
        const categoriesRes = (await API.graphql({
          query: stageQuery as any,
          variables: { orgID, nextToken },
        })) as GraphQLResult<{
          listStageCategorys: {
            items: Category[];
            nextToken: string | null;
          };
        }>;

        if (!categoriesRes.data) {
          throw new Error('Unexpected GraphQL result format');
        }

        const resItems = categoriesRes.data.listStageCategorys.items;

        // Process each category
        for (const category of resItems) {
          // Parse flexAttributes
          if (category.flexAttributes) {
            try {
              category.flexAttributes = JSON.parse(category.flexAttributes);
            } catch (e) {
              console.log(
                `Error parsing flexAttributes for category ${category.id}:`,
                e
              );
            }
          }

          // Fetch additional dispositions if needed
          await StageRepository._fetchAdditionalDispositions(category);

          // Fetch additional stages if needed
          await StageRepository._fetchAdditionalStages(category);
        }

        categories = categories.concat(resItems);
        nextToken = categoriesRes.data.listStageCategorys.nextToken || null;
      } while (nextToken);

      return categories;
    } catch (e) {
      console.log('ERROR FETCHING CATEGORIES', e);
      return [];
    }
  },

  /**
   * Fetch only the default stage category for an organization with minimal fields
   * @param {string} orgID - Organization ID
   * @returns {Promise<CategoryData | null>} - Default stage category or null if none found
   */
  fetchDefaultStageCategory: async (
    orgID: string
  ): Promise<CategoryData | null> => {
    const defaultCategoryQuery = gql`
      query GetDefaultCategory($orgID: ID!, $nextToken: String) {
        listStageCategorys(
          filter: {
            orgID: { eq: $orgID }
            isDeleted: { eq: false }
            isDefault: { eq: true }
          }
          limit: 10
          nextToken: $nextToken
        ) {
          items {
            id
            title
            isDefault
            flexAttributes
            enablePindrop
            enableDeal
            orgID
            isDeleted
          }
          nextToken
        }
      }
    `;

    try {
      // We only need the first default category — no need to paginate.
      // Paginating on large orgs (e.g. seed orgs with hundreds of categories
      // and no isDefault=true records) causes hundreds of unnecessary requests.
      const result = (await API.graphql({
        query: defaultCategoryQuery as any,
        variables: { orgID, nextToken: null },
      })) as GraphQLResult<{
        listStageCategorys: {
          items: Category[];
          nextToken: string | null;
        };
      }>;

      if (!result.data) {
        throw new Error('Unexpected GraphQL result format');
      }

      const items: Category[] = result.data.listStageCategorys.items;

      if (!items || !items.length) {
        return null;
      }

      const defaultCategory = items[0];

      // Parse flexAttributes if present
      if (
        defaultCategory.flexAttributes &&
        typeof defaultCategory.flexAttributes === 'string'
      ) {
        try {
          defaultCategory.flexAttributes = JSON.parse(
            defaultCategory.flexAttributes
          );
        } catch (e) {
          console.log(`Error parsing flexAttributes for default category:`, e);
        }
      }

      return defaultCategory;
    } catch (err) {
      console.error(
        'fetchDefaultStageCategory(): error fetching default category',
        err
      );
      return null;
    }
  },

  /**
   * Fetch additional dispositions for a category if pagination is needed
   * @private
   * @param {Category} category - The category to fetch dispositions for
   */
  _fetchAdditionalDispositions: async (category: Category): Promise<void> => {
    if (
      typeof category.dispositions === 'object' &&
      'nextToken' in category.dispositions &&
      category.dispositions.nextToken
    ) {
      let allDispositions = category.dispositions.items;
      let dispToken: string | null = category.dispositions.nextToken;

      while (dispToken) {
        const dispQuery = gql`
          query GetDispositions($categoryID: ID!, $nextToken: String) {
            listDispositions(
              filter: {
                categoryID: { eq: $categoryID }
                isDeleted: { eq: false }
              }
              nextToken: $nextToken
            ) {
              items {
                id
                categoryID
                isSetupDeal
                order
                stageID
                formName
                iconName
                isConversation
                isNotSelectable
                isDefault
                deleteType
                requireDate
                noRecording
                title
                isDeleted
              }
              nextToken
            }
          }
        `;

        const dispResult = (await API.graphql({
          query: dispQuery as any,
          variables: { categoryID: category.id, nextToken: dispToken },
        })) as GraphQLResult<{
          listDispositions: {
            items: Disposition[];
            nextToken: string | null;
          };
        }>;

        if (!dispResult.data) {
          throw new Error('Unexpected GraphQL result format');
        }

        const { items: dispItems, nextToken: newDispToken } =
          dispResult.data.listDispositions;

        allDispositions = [...allDispositions, ...dispItems];
        dispToken = newDispToken || null; // Ensure null type handling
      }

      category.dispositions = allDispositions.sort(
        (a: Disposition, b: Disposition) => a.order - b.order
      );
    } else if (
      typeof category.dispositions === 'object' &&
      'items' in category.dispositions
    ) {
      category.dispositions = category.dispositions.items
        .filter((d: Disposition) => !d.isDeleted)
        .sort((a: Disposition, b: Disposition) => a.order - b.order);
    }
  },

  /**
   * Fetch additional stages for a category if pagination is needed
   * @private
   * @param {Category} category - The category to fetch stages for
   */
  _fetchAdditionalStages: async (category: Category): Promise<void> => {
    if (
      typeof category.stages === 'object' &&
      'nextToken' in category.stages &&
      category.stages.nextToken
    ) {
      let allStages = category.stages.items;
      let stageToken: string | null = category.stages.nextToken;

      while (stageToken) {
        const stageQuery = gql`
          query GetStages($categoryID: ID!, $nextToken: String) {
            listStages(
              filter: {
                categoryID: { eq: $categoryID }
                isDeleted: { eq: false }
              }
              nextToken: $nextToken
            ) {
              items {
                id
                categoryID
                calendarAppointmentStatus
                changeToDispositionID
                description
                iconName
                isActive
                isDisplay
                isMapDefault
                isNoDealDefault
                requireCloser
                setStages
                clearStages
                sendToCRM
                userSelectable
                flexAttributes
                title
                isDeleted
                changeToDispositionID
              }
              nextToken
            }
          }
        `;

        const stageResult = (await API.graphql({
          query: stageQuery as any,
          variables: { categoryID: category.id, nextToken: stageToken },
        })) as GraphQLResult<{
          listStages: {
            items: Stage[];
            nextToken: string | null;
          };
        }>;

        if (!stageResult.data) {
          throw new Error('Unexpected GraphQL result format');
        }

        const { items: stageItems, nextToken: newStageToken } =
          stageResult.data.listStages;

        allStages = [...allStages, ...stageItems];
        stageToken = newStageToken || null; // Ensure null type handling
      }

      category.stages = allStages;
    } else if (
      typeof category.stages === 'object' &&
      'items' in category.stages
    ) {
      category.stages = category.stages.items.filter(
        (s: Stage) => !s.isDeleted
      );
    }
  },

  /**
   * Create a new stage category
   * @param {CategoryData} categoryData - The data for the new category
   * @returns {Promise<Object>} - The created category
   */
  createStageCategory: async (categoryData: CategoryData): Promise<any> => {
    try {
      // NOTE: Replace this implementation when the actual graphql/mutations module is available
      const result = (await API.graphql(
        graphqlOperation(createStageCategory, { input: categoryData })
      )) as GraphQLResult<{
        createStageCategory: Category;
      }>;

      if (!result.data) {
        throw new Error('Unexpected GraphQL result format');
      }

      return result.data.createStageCategory;
    } catch (error) {
      console.error('Error creating stage category:', error);
      throw error;
    }
  },

  /**
   * Update a stage category
   * @param {CategoryData} categoryData - The data to update
   * @returns {Promise<Object>} - The updated category
   */
  updateStageCategory: async (categoryData: CategoryData): Promise<any> => {
    try {
      // NOTE: Replace this implementation when the actual graphql/mutations module is available
      const result = (await API.graphql(
        graphqlOperation(updateStageCategory, { input: categoryData })
      )) as GraphQLResult<{
        updateStageCategory: Category;
      }>;

      if (!result.data) {
        throw new Error('Unexpected GraphQL result format');
      }

      return result.data.updateStageCategory;
    } catch (error) {
      console.error('Error updating stage category:', error);
      throw error;
    }
  },
};

export default StageRepository;
